import React from 'react';
import { FiAlertTriangle, FiRotateCcw } from 'react-icons/fi';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import {
  receiveLineProduct,
  receiveLineStatusLabel,
  type ReceiveLine
} from '../../receiveScan';

type ReceiveLineRowProps = {
  key?: string;
  line: ReceiveLine;
  flash: 'a' | 'b' | null;
  onUndo: (line: ReceiveLine) => void;
};

/**
 * One product on the receive checklist. An open row shows ordered and shipped quantities. A
 * checked row shows who confirmed it and an Undo. A product the EPCIS never sent cannot be scanned.
 * A product whose EPCIS listed more units than the PO ordered says so; nothing is bound to a serial
 * here — stocking decides which units arrived.
 */
function ReceiveLineRow({ line, flash, onUndo }: ReceiveLineRowProps) {
  const confirmed = line.status === 'confirmed';
  const missing = line.status === 'notInShipment';
  const statusLabel = receiveLineStatusLabel(line);
  const overage = line.epcisOverageQuantity || 0;

  return (
    <div
      className={[
        'receive-line',
        confirmed && 'receive-line--confirmed',
        missing && 'receive-line--missing',
        flash && `receive-line--flash-${flash}`
      ]
        .filter(Boolean)
        .join(' ')}
      data-product-id={line.productId}
    >
      <div className="receive-line__main">
        <div className="receive-line__product">
          <WarehouseProductDisplay product={receiveLineProduct(line)} />
        </div>
        <div className="receive-line__side">
          {statusLabel ? (
            <span
              className={[
                'receive-line__status',
                confirmed ? 'receive-line__status--confirmed' : 'receive-line__status--missing'
              ].join(' ')}
            >
              {statusLabel}
            </span>
          ) : (
            <span className="receive-line__qty">
              <small>Shipped</small>
              {line.shippedQuantity}
              <small className="receive-line__ordered">of {line.orderedQuantity}</small>
            </span>
          )}
        </div>
      </div>
      {overage > 0 && (
        <div className="receive-line__overage">
          <FiAlertTriangle aria-hidden /> EPCIS over PO by {overage}
        </div>
      )}
      {confirmed && (
        <div className="receive-line__actions">
          <button type="button" className="receive-line__action" onClick={() => onUndo(line)}>
            <FiRotateCcw aria-hidden /> Undo
          </button>
          {line.confirmedByName && (
            <span className="receive-line__confirmed">{line.confirmedByName}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default ReceiveLineRow;
